/**
 * The notation the tracks are written in: jianpu (简谱), one line per part, tokens apart by spaces.
 *
 *   5  6_ 1'_  3'--  |  2'. 1'_ 6 5~  ;  (6)5^ 0
 *
 * Notes
 *   1–7        the degree (1 = 宫 of the track's key; 4 and 7 are passing tones), `#`/`b` before it
 *   ' ,        an octave up / down (repeatable): 1' 6,,
 *   a&b        played together: 1,&5,
 *   (x)        a grace note (倚音) just before: (6)5
 * Lengths (one beat by default)
 *   _          half as long (eighth; __ sixteenth)     .   dotted (× 1.5)     -   one beat more
 * Techniques
 *   ~ 揉 vibrato      ^ 按 pressed up into the note     / 绰 slide up into it     \ 注 slide down off it
 *   * 泛音 harmonic   % 摇指 tremolo     g 刮奏 glissando up into it     G glissando down into it
 *   ! accent         p soft
 * Other tokens
 *   0          a rest (with lengths: 0- 0_)
 *   ;          a breath: the flute or erhu phrase ends here
 *   @pp … @ff  dynamics from here on (pp p mp mf f ff)
 *   |          a bar line, for the eye only
 * Percussion parts use letters instead of degrees (see `DRUMS` in samples.ts): D d k t c G g B b h w.
 */

export type Tech = 'rou' | 'an' | 'chuo' | 'zhu' | 'fan' | 'trem' | 'glissUp' | 'glissDown';

export interface Pitch {
  readonly degree: number;
  readonly octave: number;
  readonly acc: number;
}

export interface NoteTok {
  readonly kind: 'note';
  readonly pitches: readonly Pitch[];
  readonly beats: number;
  readonly techs: readonly Tech[];
  /** Accent (> 1) or soft (< 1). */
  readonly accent: number;
  /** Level from the last dynamics mark. */
  readonly dyn: number;
  readonly grace?: Pitch;
}

export interface HitTok {
  readonly kind: 'hit';
  readonly drums: readonly string[];
  readonly beats: number;
  readonly accent: number;
  readonly dyn: number;
}

export interface RestTok {
  readonly kind: 'rest';
  readonly beats: number;
}

export interface BreathTok {
  readonly kind: 'breath';
}

export type Tok = NoteTok | HitTok | RestTok | BreathTok;

const TECH: Record<string, Tech> = {
  '~': 'rou',
  '^': 'an',
  '/': 'chuo',
  '\\': 'zhu',
  '*': 'fan',
  '%': 'trem',
  g: 'glissUp',
  G: 'glissDown',
};

export const DYNAMICS: Record<string, number> = { pp: 0.35, p: 0.5, mp: 0.65, mf: 0.8, f: 0.95, ff: 1.1 };

const PITCH = /^([#b]?)([1-7])([',]*)$/;

function pitch(src: string, where: string): Pitch {
  const m = PITCH.exec(src);
  if (!m) throw new Error(`bad note "${src}" in ${where}`);
  let octave = 0;
  for (const c of m[3]!) octave += c === "'" ? 1 : -1;
  return { degree: Number(m[2]), octave, acc: m[1] === '#' ? 1 : m[1] === 'b' ? -1 : 0 };
}

/** Length in beats and the marks of a token's tail (`_.-~^…`). */
function tail(src: string, where: string): { beats: number; techs: Tech[]; accent: number } {
  let halves = 0;
  let dots = 0;
  let dashes = 0;
  let accent = 1;
  const techs: Tech[] = [];
  for (const c of src) {
    if (c === '_') halves++;
    else if (c === '.') dots++;
    else if (c === '-') dashes++;
    else if (c === '!') accent *= 1.3;
    else if (c === 'p') accent *= 0.6;
    else if (TECH[c]) techs.push(TECH[c]);
    else throw new Error(`bad mark "${c}" in "${where}"`);
  }
  if (dots > 1) throw new Error(`one dot at most in "${where}"`);
  const beats = 0.5 ** halves * (dots ? 1.5 : 1) + dashes;
  return { beats, techs, accent };
}

/** Parses one part's line. `perc` reads letters as drums instead of degrees. */
export function parseLine(line: string, perc = false): Tok[] {
  const out: Tok[] = [];
  let dyn = DYNAMICS.mf!;
  for (const raw of line.split(/\s+/)) {
    const tok = raw.trim();
    if (!tok || tok === '|') continue;
    if (tok === ';') {
      out.push({ kind: 'breath' });
      continue;
    }
    if (tok.startsWith('@')) {
      const d = DYNAMICS[tok.slice(1)];
      if (d === undefined) throw new Error(`bad dynamics "${tok}"`);
      dyn = d;
      continue;
    }
    if (tok[0] === '0') {
      const t = tail(tok.slice(1), tok);
      if (t.techs.length) throw new Error(`a rest has no technique: "${tok}"`);
      out.push({ kind: 'rest', beats: t.beats });
      continue;
    }
    if (perc) {
      const m = /^([A-Za-z](?:&[A-Za-z])*)(.*)$/.exec(tok);
      if (!m) throw new Error(`bad hit "${tok}"`);
      const t = tail(m[2]!, tok);
      out.push({ kind: 'hit', drums: m[1]!.split('&'), beats: t.beats, accent: t.accent, dyn });
      continue;
    }
    const m = /^(?:\(([^)]+)\))?([#b]?[1-7][',]*(?:&[#b]?[1-7][',]*)*)(.*)$/.exec(tok);
    if (!m) throw new Error(`bad note "${tok}"`);
    const t = tail(m[3]!, tok);
    out.push({
      kind: 'note',
      pitches: m[2]!.split('&').map((p) => pitch(p, tok)),
      beats: t.beats,
      techs: t.techs,
      accent: t.accent,
      dyn,
      ...(m[1] ? { grace: pitch(m[1], tok) } : {}),
    });
  }
  return out;
}

/** Total length of a parsed line, in beats. */
export const lineBeats = (toks: readonly Tok[]): number =>
  toks.reduce((n, t) => n + (t.kind === 'breath' ? 0 : t.beats), 0);
